import { useBadges, BADGE_IMAGES, BADGE_METADATA, UserBadge, OfficialBadgeType } from "@/hooks/useBadges";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface UserBadgesProps {
  walletAddress?: string;
  showLabels?: boolean;
  maxDisplay?: number;
}

export function UserBadges({ walletAddress, showLabels = false, maxDisplay = 5 }: UserBadgesProps) {
  const { badges, loading } = useBadges(walletAddress);

  if (loading) {
    return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  }

  if (badges.length === 0) {
    return null;
  }

  const displayedBadges = badges.slice(0, maxDisplay);
  const remainingCount = badges.length - maxDisplay;

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2 flex-wrap">
        {displayedBadges.map((badge) => (
          <BadgeItem key={badge.id} badge={badge} showLabel={showLabels} />
        ))}
        {remainingCount > 0 && (
          <Tooltip>
            <TooltipTrigger>
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                +{remainingCount}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{remainingCount} more badge{remainingCount > 1 ? "s" : ""}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}

function BadgeItem({ badge, showLabel }: { badge: UserBadge; showLabel: boolean }) {
  const badgeType = badge.badge_type as OfficialBadgeType;
  const imageUrl = BADGE_IMAGES[badgeType] || badge.image_url;
  const metadata = BADGE_METADATA[badgeType];

  return (
    <Tooltip>
      <TooltipTrigger>
        <div className="relative group">
          <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-primary/50 ring-offset-2 ring-offset-background transition-all group-hover:ring-primary group-hover:scale-110">
            {imageUrl ? (
              <img 
                src={imageUrl} 
                alt={badge.badge_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-primary-foreground text-xs font-bold">
                {badge.badge_name.charAt(0)}
              </div>
            )}
          </div>
          {showLabel && (
            <span className="text-xs mt-1 block text-center">{badge.badge_name}</span>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs p-0 overflow-hidden">
        <div className="relative">
          {imageUrl && (
            <div className="w-48 h-32 overflow-hidden">
              <img 
                src={imageUrl} 
                alt={badge.badge_name}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="p-3 bg-gradient-to-t from-background to-background/80">
            <p className="font-bold text-sm">{badge.badge_name}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {metadata?.description || badge.badge_description}
            </p>
            {badge.earned_at && (
              <p className="text-xs text-muted-foreground/60 mt-2">
                Earned {formatDistanceToNow(new Date(badge.earned_at), { addSuffix: true })}
              </p>
            )}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export function BadgeDisplay({ badges }: { badges: UserBadge[] }) {
  if (badges.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-3">
      {badges.map((badge) => {
        const badgeType = badge.badge_type as OfficialBadgeType;
        const imageUrl = BADGE_IMAGES[badgeType] || badge.image_url;
        const metadata = BADGE_METADATA[badgeType];

        return (
          <TooltipProvider key={badge.id}>
            <Tooltip>
              <TooltipTrigger>
                <div className="relative group">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl overflow-hidden ring-2 ring-primary/30 ring-offset-2 ring-offset-background transition-all group-hover:ring-primary group-hover:scale-105 shadow-lg">
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={badge.badge_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
                        <span className="text-xl font-bold text-primary-foreground">
                          {badge.badge_name.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs p-0 overflow-hidden">
                <div className="relative">
                  {imageUrl && (
                    <div className="w-48 h-32 overflow-hidden">
                      <img 
                        src={imageUrl} 
                        alt={badge.badge_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-3 bg-gradient-to-t from-background to-background/80">
                    <p className="font-bold text-sm">{badge.badge_name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {metadata?.description || badge.badge_description}
                    </p>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}
